import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Boxes, ChartLine, Coins, Stethoscope } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { CardContent, CardIcon } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ListCard } from '../ListCard'
import { ListCardHeader } from '../ListCardHeader'
import { FloorPricesPanel } from './FloorPricesPanel'
import { BidHistoryPanel } from './BidHistoryPanel'
import { InventoryPanel, inventoryScopeKey, useInventoryCoverage } from './InventoryPanel'
import {
  LineItemDiagnosticsApi,
  type FloorPrice,
  type FloorPricePublisher,
  type SiteBidHistory,
} from '@/services/lineItems/lineItemDiagnosticsApi'
import { getContext } from '@/services/core/context'
import { AuthService } from '@/services/core/auth'
import { formatMoneyRange } from '@/utils/money'
import type { Targeting } from '@/services/targeting/targeting'

export interface LineItemDiagnosticsCardProps {
  lineItemId: string
  /** Bid history only exists for line items that bid dynamically. */
  showBidHistory: boolean
  /** The targeting as edited, which the inventory check forecasts for. */
  targeting: Targeting
  /** Sizes the line item's creatives already cover, as `320x400`. */
  creativeSizes: string[]
}

/** Every floor price at every level, with how many sites they cover. */
function summariseFloorPrices(publishers: FloorPricePublisher[]) {
  const prices: FloorPrice[] = []
  let sites = 0
  for (const pub of publishers) {
    prices.push(...pub.floorPrices)
    for (const site of pub.sites) {
      sites++
      prices.push(...site.floorPrices)
      for (const unit of site.adUnits) prices.push(...unit.floorPrices)
    }
  }
  const amounts = prices.map((p) => p.price.amount)
  return {
    sites,
    range:
      amounts.length > 0
        ? formatMoneyRange(Math.min(...amounts), Math.max(...amounts), prices[0].price.currency)
        : null,
  }
}

/** The latest bid range on each site, spanning lowest lower bound to highest upper. */
function summariseBids(history: SiteBidHistory[]) {
  const latest = history.flatMap((h) => h.updates.at(-1) ?? [])
  const lows = latest.flatMap((u) => u.lowerCpm?.amount ?? [])
  const highs = latest.flatMap((u) => u.upperCpm?.amount ?? [])
  const currency = latest.find((u) => u.upperCpm)?.upperCpm?.currency
  return {
    sites: history.length,
    range:
      lows.length > 0 && highs.length > 0
        ? formatMoneyRange(Math.min(...lows), Math.max(...highs), currency)
        : null,
  }
}

/**
 * Tools for working out why a line item delivers the way it does: the floor
 * prices it has to beat, for dynamic bidding the bids it has been placing, and
 * the creative sizes its targeting reaches. One tab each.
 */
export function LineItemDiagnosticsCard({
  lineItemId,
  showBidHistory,
  targeting,
  creativeSizes,
}: LineItemDiagnosticsCardProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'floorPrices' | 'bidHistory' | 'inventory'>('floorPrices')
  // The inventory forecast is costly, so it waits until its tab is first opened.
  const [inventoryRequested, setInventoryRequested] = useState(false)
  const inventory = useInventoryCoverage(targeting, creativeSizes, inventoryRequested)
  const context = getContext()
  const enabled = AuthService.isAuthenticated() && !!lineItemId

  const floorPrices = useQuery({
    queryKey: ['floorPrices', 'lineItem', context, lineItemId],
    queryFn: () => LineItemDiagnosticsApi.getFloorPrices(lineItemId),
    enabled,
    staleTime: 5 * 60 * 1000,
  })

  const bidHistory = useQuery({
    queryKey: ['bidHistory', 'lineItem', context, lineItemId],
    queryFn: () => LineItemDiagnosticsApi.getBidHistory(lineItemId),
    enabled: enabled && showBidHistory,
    staleTime: 60 * 1000,
  })

  const floorSummary = useMemo(
    () => summariseFloorPrices(floorPrices.data ?? []),
    [floorPrices.data]
  )
  const bidSummary = useMemo(() => summariseBids(bidHistory.data ?? []), [bidHistory.data])

  const [scopeKey, scopeOptions] = inventoryScopeKey(inventory)

  return (
    <ListCard>
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as typeof tab)}
        className="gap-0"
      >
        <ListCardHeader
          className="pb-3"
          title={
            <>
              <CardIcon icon={Stethoscope} />
              {t('lineItemDiagnostics.title')}
            </>
          }
          actions={
            <TabsList>
              <TabsTrigger value="floorPrices">
                <Coins />
                {t('lineItemDiagnostics.floorPrices')}
                {floorSummary.sites > 0 && (
                  <Badge variant="secondary">{floorSummary.sites}</Badge>
                )}
              </TabsTrigger>
              {showBidHistory && (
                <TabsTrigger value="bidHistory">
                  <ChartLine />
                  {t('lineItemDiagnostics.bidHistory')}
                  {bidSummary.sites > 0 && (
                    <Badge variant="secondary">{bidSummary.sites}</Badge>
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger
                value="inventory"
                disabled={!inventory.canCheck}
                // The forecast is only run once its tab is actually opened.
                onClick={() => setInventoryRequested(true)}
              >
                <Boxes />
                {t('inventory.title')}
              </TabsTrigger>
            </TabsList>
          }
        />

        <CardContent className="pt-0">
          <Separator className="mb-4" />

          <TabsContent value="floorPrices">
            <FloorPricesPanel
              publishers={floorPrices.data ?? []}
              isLoading={floorPrices.isLoading}
              isError={floorPrices.isError}
            />
          </TabsContent>

          {showBidHistory && (
            <TabsContent value="bidHistory">
              <BidHistoryPanel
                lineItemId={lineItemId}
                history={bidHistory.data ?? []}
                isLoading={bidHistory.isLoading}
                isError={bidHistory.isError}
                active={tab === 'bidHistory'}
              />
            </TabsContent>
          )}

          <TabsContent value="inventory">
            {inventory.canCheck ? (
              <InventoryPanel coverage={inventory} />
            ) : (
              <p className="text-sm text-(--ad-text-muted)">{t(scopeKey, scopeOptions)}</p>
            )}
          </TabsContent>
        </CardContent>
      </Tabs>
    </ListCard>
  )
}
